/**
 * Lightweight navigation trail.
 *
 * Records the paths the user has visited in this tab so any screen can offer a
 * contextual "Back to <parent>" action even when the incoming link did not
 * attach an explicit dashboard-origin marker. It is presentation context only:
 * it never affects permissions, RLS, or which records a screen may load, and it
 * never pushes history entries (returning always uses history.back()).
 */
import { useEffect } from "react";
import { useLocation } from "@tanstack/react-router";

const KEY = "mr:nav-trail";
/** Interstitials are never useful "back" destinations. */
const IGNORED = ["/onboarding", "/login", "/signup", "/auth", "/logout"];
const MAX = 12;

let trail: string[] | null = null;

function read(): string[] {
  if (trail) return trail;
  trail = [];
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) trail = parsed.filter((p) => typeof p === "string");
    }
  } catch {
    /* private mode / storage disabled — in-memory trail still works */
  }
  return trail;
}

function persist(next: string[]) {
  trail = next;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/** Record path changes. Mounted once, inside the authenticated shell. */
export function useNavTrail() {
  const pathname = useLocation({ select: (l) => l.pathname });
  useEffect(() => {
    if (IGNORED.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return;
    const current = read();
    if (current[current.length - 1] === pathname) return;
    const next = [...current, pathname];
    persist(next.slice(Math.max(0, next.length - MAX)));
  }, [pathname]);
}

/** The most recent path that differs from `current`, if any. */
export function previousPath(current: string): string | null {
  const current_ = read();
  for (let i = current_.length - 1; i >= 0; i -= 1) {
    if (current_[i] !== current) return current_[i]!;
  }
  return null;
}
